/**
 * Faults on demand, so the screens nobody can reproduce can be looked at.
 *
 * The three worst things this app does are a crash, a local database that will
 * not open, and one that opens so slowly it amounts to the same. All three put
 * a screen in front of somebody and send a report — and all three are almost
 * impossible to bring about on purpose.
 *
 * DEVELOPMENT ONLY. `import.meta.env.DEV` is a compile-time constant that Vite
 * replaces with `false` in a build, so everything below folds away. It is
 * deliberately not a setting and not a route.
 *
 * Use it by adding `?fault=…` to the address:
 *   ?fault=crash      — a component throws while rendering
 *   ?fault=db-fail    — opening the local database rejects
 *   ?fault=db-stall   — opening it never finishes (the slow notice)
 *   ?fault=db-blocked — …and a held database is the reason (the notice that
 *                       offers a way out). Its automatic recovery is left off
 *                       here: looking at the screen should not hand this device
 *                       a new database generation.
 */

const FAULTS = ['crash', 'db-fail', 'db-stall', 'db-blocked'] as const;

export type DevFault = (typeof FAULTS)[number];

export function requestedFault(): DevFault | null {
    if (!import.meta.env.DEV || typeof window === 'undefined') {
        return null;
    }
    const asked = new URLSearchParams(window.location.search).get('fault');
    return FAULTS.includes(asked as DevFault) ? (asked as DevFault) : null;
}

/**
 * Stand in for opening the local database when one of its faults was asked
 * for. Returns null when it was not, so the caller opens normally.
 */
export function injectedDatabaseFault(): Promise<never> | null {
    const fault = requestedFault();
    if (fault === 'db-fail') {
        return Promise.reject(
            new Error('Testfehler: die lokale Datenbank wurde nicht geöffnet')
        );
    }
    if (fault === 'db-stall' || fault === 'db-blocked') {
        // Never settles — the same shape as the real stall, where the open is
        // still running and the notice appears beside it.
        return new Promise<never>(() => {});
    }
    return null;
}

/**
 * The verdict `classifyStall` should reach, when one was asked for. The real
 * one probes the storage; a fault cannot stage a held database convincingly,
 * so it says what the screen is meant to show.
 */
export function forcedStallKind(): 'slow' | 'blocked' | null {
    const fault = requestedFault();
    if (fault === 'db-blocked') {
        return 'blocked';
    }
    if (fault === 'db-stall') {
        return 'slow';
    }
    return null;
}

/** Whether the screen on show came from `?fault=…` rather than from a device. */
export function isStagedFault(): boolean {
    return requestedFault() !== null;
}
